import React, { useEffect, useState } from "react";
import { AnimatePresence, motion, MotionConfig } from "framer-motion";
import { AlertTriangle, Ban, Check, Loader2, Lock, MapPin, Sparkles } from "lucide-react";

import { analyseTenderFit } from "../../../services/tendersApi";
import PrecedentBlock from "../../../components/PrecedentBlock/PrecedentBlock";

/* Is this one worth your time?
 *
 * TWO HALVES WITH DIFFERENT COSTS, and the screen shows that honestly. The gates
 * arrive with the tender — free, objective, no model — so an obstacle is on
 * screen the moment the page opens, which is the only moment it saves anybody
 * anything. The verdict costs a model call, so it sits behind a button.
 *
 * A GATE NEVER HIDES THE BUTTON. It did at first - "nothing left to decide" - and
 * a notice whose deadline had passed showed the gate and nothing else, while
 * /recall on the same notice gave a verdict, the precedent and "you'd need". The
 * gates are context for the read, not a reason to withhold it.
 *
 * THE BUTTON HAS TO BE FOUND. The first version was a white ghost button under a
 * grey sentence, in a white card among white cards — the one feature on the page
 * that can save a day looked like a footnote. So: the card carries a faint brand
 * tint so it reads as a distinct thing, the action is the page's primary button
 * in the app's own colour, and the three things it compares are named as chips
 * a person can scan rather than a sentence they have to read.
 *
 * THE WAIT IS SHOWN, NOT HIDDEN. The read takes three to twelve seconds
 * (measured), long enough for a person to wonder whether the click registered.
 * A spinner alone says "something is happening"; a short list of stages ticking
 * through says WHAT is happening, and a wait with visible progress is felt as
 * shorter than the same wait with none. The stages are paced to the measured
 * latency, not to the request — a promise of shape, not a progress bar of fact
 * — and the last one holds until the answer lands.
 *
 * Same four verdicts and the same reasons / "you'd need" shape as /recall in
 * Sensa chat. A bid manager should not have to learn two vocabularies for one
 * question, and matching wording means the two screens cannot quietly disagree.
 */

const VERDICTS = {
  "Worth a look": { tone: "text-[#1F7D38]", dot: "bg-[#2DAD4E]" },
  "Problems to resolve first": { tone: "text-[#7A5209]", dot: "bg-[#D9A441]" },
  "Probably not worth the time": { tone: "text-[#8A3F3F]", dot: "bg-[#B4655F]" },
  "Not enough information yet": { tone: "text-[#71717A]", dot: "bg-[#C4C4C8]" },
};

const GATE_ICON = {
  set_aside: Ban,
  geography: MapPin,
  // The award is already pointed at one supplier. A padlock, not a warning
  // triangle: nothing has gone wrong, the door is simply shut.
  restriction: Lock,
};

// Blocking reads as a wall, caution as a note. `ok` is deliberately quiet: a
// set-aside you DO qualify for is worth confirming once, not highlighting.
const GATE_TONE = {
  blocking: "border-[#B91C1C]/20 bg-[#B91C1C]/[0.04] text-[#B91C1C]",
  caution: "border-[#D9A441]/25 bg-[#D9A441]/[0.06] text-[#7A5209]",
  ok: "border-[#E5E7EB] bg-[#FAFAFA] text-[#52525B]",
};

// What the read is doing, in the order it does it, paced to the measured
// latency: 1.6 s a step reaches the last one at ~5 s, inside the typical wait,
// and holds there. Never shown as finished until the answer actually arrives.
const STAGES = [
  "Reading the notice",
  "Checking what your company does",
  "Looking at bids you've already closed",
  "Writing a verdict",
];
const STAGE_MS = 1600;

// The three inputs, as chips rather than a sentence. A person scans three
// labels in the time it takes to start reading a clause.
const INPUTS = ["What you do", "What you've delivered", "Bids you've closed"];

const EASE = [0.22, 1, 0.36, 1];

const Line = ({ children, delay = 0 }) => (
  <motion.li
    className="flex gap-2.5 py-[3px]"
    initial={{ opacity: 0, y: 4 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.22, ease: EASE, delay }}
  >
    <span className="mt-[9px] h-[3px] w-[3px] flex-none rounded-full bg-[#C4C4C8]" />
    <span className="min-w-0 flex-1 font-outfit text-[14px] leading-relaxed text-[#3F3F46]">
      {children}
    </span>
  </motion.li>
);

function Gate({ gate }) {
  const Icon = GATE_ICON[gate.kind] || AlertTriangle;
  return (
    <div
      className={`flex gap-3 rounded-lg border px-3.5 py-3 ${
        GATE_TONE[gate.severity] || GATE_TONE.caution
      }`}
    >
      <Icon className="mt-[2px] h-4 w-4 flex-none" />
      <div className="min-w-0 font-outfit">
        <p className="text-[14px] font-semibold leading-snug">{gate.headline}</p>
        {gate.detail && (
          <p className="mt-0.5 text-[13px] leading-relaxed opacity-80">{gate.detail}</p>
        )}
        {/* The agency's own sentence, lifted verbatim — never written by a model,
            because a paraphrase inside quotation marks is a fabricated citation.
            A finding that tells somebody not to spend a day on a bid should be
            checkable without opening SAM.gov. */}
        {gate.quote && (
          <blockquote className="mt-2 border-l-2 border-current/25 pl-2.5">
            <p className="text-[13px] italic leading-relaxed opacity-90">
              &ldquo;{gate.quote}&rdquo;
            </p>
            {gate.quote_source && (
              <cite className="mt-0.5 block text-[11.5px] not-italic opacity-60">
                {gate.quote_source}
              </cite>
            )}
          </blockquote>
        )}
      </div>
    </div>
  );
}

/* The wait, made legible. Steps tick through on a timer paced to how long the
   read usually takes; a thin shimmer under them keeps moving so a slow response
   never looks like a stalled one. */
function Progress({ stage }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2, ease: EASE }}
      className="mt-4"
      aria-live="polite"
    >
      <ul className="space-y-1.5">
        {STAGES.map((label, i) => {
          const done = i < stage;
          const now = i === stage;
          return (
            <li
              key={label}
              className={`flex items-center gap-2.5 font-outfit text-[13.5px] transition-colors ${
                now ? "text-[#18101C]" : done ? "text-[#8A8A94]" : "text-[#C4C4C8]"
              }`}
            >
              <span className="flex h-4 w-4 flex-none items-center justify-center">
                {done ? (
                  <motion.span
                    initial={{ scale: 0.6, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.18, ease: EASE }}
                  >
                    <Check className="h-3.5 w-3.5 text-[#2DAD4E]" />
                  </motion.span>
                ) : now ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-[#C95EBE]" />
                ) : (
                  <span className="h-[5px] w-[5px] rounded-full bg-current" />
                )}
              </span>
              {label}
              {now ? "…" : ""}
            </li>
          );
        })}
      </ul>

      <div className="mt-3 h-[3px] w-full overflow-hidden rounded-full bg-[#C95EBE]/10">
        <motion.div
          className="h-full w-1/3 rounded-full bg-[#C95EBE]/60"
          animate={{ x: ["-100%", "300%"] }}
          transition={{ duration: 1.4, ease: "easeInOut", repeat: Infinity }}
        />
      </div>
    </motion.div>
  );
}

export default function TenderFitCard({ tender }) {
  const [fit, setFit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stage, setStage] = useState(0);

  // The deadline gate is not drawn here. The overview card directly above
  // already shows "Closes today" in red with the exact time, so repeating it
  // put the same fact on screen twice within a scroll's height. It still
  // reaches the model as `already_shown`, which is what stops the verdict from
  // restating it - that is the gate's job on this page, not being visible.
  const gates = (tender?.gates || []).filter((g) => g.kind !== "deadline");

  // Advance the stages while the request is in flight; hold at the last one.
  // The timer is cleared the moment loading ends, so a fast answer never shows
  // a stage the read skipped.
  useEffect(() => {
    if (!loading) return undefined;
    setStage(0);
    const id = setInterval(
      () => setStage((s) => Math.min(s + 1, STAGES.length - 1)),
      STAGE_MS
    );
    return () => clearInterval(id);
  }, [loading]);

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      setFit(await analyseTenderFit(tender.id));
    } catch (e) {
      setError(
        e?.response?.data?.error || "Couldn't check this against your Memory."
      );
    } finally {
      setLoading(false);
    }
  };

  const style = VERDICTS[fit?.verdict] || VERDICTS["Not enough information yet"];

  return (
    <MotionConfig reducedMotion="user">
      {/* A faint brand tint on the card itself. Every other section of this
          page is a white card on a grey canvas, and this one was invisible
          among them; the tint says "this one does something" without shouting. */}
      <section className="mb-4 rounded-xl border border-[#C95EBE]/25 bg-gradient-to-br from-[#FDF8FC] to-white px-6 py-5">
        <div className="mb-3 flex items-start gap-2.5">
          <span className="mt-[3px] flex h-6 w-6 flex-none items-center justify-center rounded-full bg-[#C95EBE]/10">
            <Sparkles className="h-3.5 w-3.5 text-[#C95EBE]" />
          </span>
          <div className="font-outfit">
            <h2 className="text-[15px] font-semibold text-[#18101C]">
              Is this worth your time?
            </h2>
            <p className="mt-0.5 text-[13px] leading-relaxed text-[#71717A]">
              A short read on how this notice fits your company, from what your
              workspace already knows.
            </p>
          </div>
        </div>

        {!!gates.length && (
          <div className="space-y-2">
            {gates.map((g, i) => (
              <Gate key={i} gate={g} />
            ))}
          </div>
        )}

        <AnimatePresence mode="wait" initial={false}>
          {!fit ? (
            <motion.div
              key="ask"
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.18, ease: EASE }}
              className={gates.length ? "mt-4" : "mt-1"}
            >
              <button
                type="button"
                onClick={run}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg bg-[#C95EBE] px-4 py-2.5 font-outfit
                           text-[14px] font-medium text-white shadow-sm transition-all
                           hover:bg-[#B84FAD] active:scale-[0.99]
                           disabled:cursor-wait disabled:opacity-80"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                {loading ? "Checking…" : "Check against your Memory"}
              </button>

              {loading ? (
                <Progress stage={stage} />
              ) : (
                <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1.5 font-outfit">
                  <span className="text-[12.5px] text-[#8A8A94]">Compares it with</span>
                  {INPUTS.map((label) => (
                    <span
                      key={label}
                      className="rounded-full border border-black/[0.08] bg-white px-2.5 py-[3px] text-[12.5px] text-[#52525B]"
                    >
                      {label}
                    </span>
                  ))}
                </div>
              )}

              {error && (
                <p className="mt-2 font-outfit text-[13px] text-[#B91C1C]">{error}</p>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="verdict"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.24, ease: EASE }}
              className={gates.length ? "mt-4 border-t border-black/[0.06] pt-4" : "mt-2"}
            >
              <div className="flex items-center gap-2">
                <motion.span
                  className={`h-[7px] w-[7px] flex-none rounded-full ${style.dot}`}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 500, damping: 30, delay: 0.1 }}
                />
                <p className={`font-outfit text-[16px] font-semibold ${style.tone}`}>
                  {fit.verdict}
                </p>
              </div>

              {!!fit.reasons?.length && (
                <ul className="mt-2.5">
                  {fit.reasons.map((r, i) => (
                    <Line key={i} delay={0.08 + i * 0.05}>{r}</Line>
                  ))}
                </ul>
              )}

              {/* A fact from the reader's own record, not a judgement from the
                  model - so it gets its own block rather than a bullet among
                  the reasons. Only appears when a closed bid genuinely matches. */}
              <PrecedentBlock items={fit.last_time} delay={0.2} />

              {!!fit.youd_need?.length && (
                <motion.div
                  className="mt-3.5 border-t border-black/[0.06] pt-3"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2, delay: 0.3 }}
                >
                  <p className="mb-1 font-outfit text-[13px] font-medium text-[#52525B]">
                    You&apos;d need
                  </p>
                  <ul>
                    {fit.youd_need.map((n, i) => (
                      <Line key={`n-${i}`} delay={0.34 + i * 0.05}>{n}</Line>
                    ))}
                  </ul>
                </motion.div>
              )}

              {!fit.verdict && !fit.reasons?.length && (
                <p className="font-outfit text-[13.5px] leading-relaxed text-[#8A8A94]">
                  There isn&apos;t enough on file about this notice or your company to
                  say anything useful yet.
                </p>
              )}

              {/* Said once, at the bottom, because a thin profile is the most
                  common reason this card is disappointing and the user can fix it. */}
              {fit.memory_ready === false && (
                <p className="mt-3 font-outfit text-[13px] leading-relaxed text-[#8A8A94]">
                  Your workspace Memory has no capabilities recorded yet, so this is
                  working from very little. Knowledge Base → Memory.
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </section>
    </MotionConfig>
  );
}
